"use client";

import React from "react";
import { 
  ShieldCheck, 
  Lock, 
  KeyRound, 
  FileCheck, 
  Server, 
  HeartHandshake, 
  CheckCircle2, 
  Sparkles,
  Database,
  UserCheck
} from "lucide-react";

export function TrustSecuritySection({ onOpenLegal }: { onOpenLegal: (doc: "privacy" | "terms" | "safeguarding" | "copyright") => void }) {
  const securityPillars = [
    {
      title: "DPDP Act 2023 Minor Protection",
      description: "Strictly aligns with Section 9 of India's Digital Personal Data Protection Act 2023. Explicit, verifiable digital consent is recorded from parents before conducting athletic tests or storing minor health records.",
      icon: ShieldCheck,
      badge: "Statutory Law",
      color: "text-cyan-400 border-cyan-500/30 bg-cyan-500/10",
      legalDoc: "privacy" as const
    },
    {
      title: "Cryptographic Gate Tickets (HMAC-SHA256)",
      description: "After-hours facility visitors receive single-use cryptographically signed QR tickets. Verified offline by security guards via mobile cameras; tamper-proof and resistant to screenshot duplication.",
      icon: KeyRound,
      badge: "Zero-Trust Access",
      color: "text-emerald-400 border-emerald-500/30 bg-emerald-500/10",
      legalDoc: "terms" as const
    },
    {
      title: "AAP Pre-Participation Medical Gate",
      description: "American Academy of Pediatrics (AAP) and PAR-Q+ protocols embedded directly in software. Students with cardiac flags, asthma, or previous concussions are automatically restricted from maximal testing.",
      icon: HeartHandshake,
      badge: "Clinical Safety",
      color: "text-rose-400 border-rose-500/30 bg-rose-500/10",
      legalDoc: "safeguarding" as const
    },
    {
      title: "Multi-Tenant Row-Level Security (RLS)",
      description: "PostgreSQL database enforces cryptographic isolation between schools via Row-Level Security policies. One school can never see another school's rosters, medical evaluations, or financial ledger.",
      icon: Database,
      badge: "Tenant Isolation",
      color: "text-purple-400 border-purple-500/30 bg-purple-500/10",
      legalDoc: "privacy" as const
    },
    {
      title: "Damage Control Escrow & Zero Liability",
      description: "Community slot renters provide pre-authorized security deposits. Pre-use and post-use digital photographic inspections guarantee that any broken equipment or property damage is billed to the visitor, not the school.",
      icon: Lock,
      badge: "Financial Protection",
      color: "text-amber-400 border-amber-500/30 bg-amber-500/10",
      legalDoc: "terms" as const
    },
    {
      title: "Strict Academic Priority Shield",
      description: "Intra-school athletic training, curricular physical education, and annual sports days hold absolute hard-coded priority. External rentals can never overlap with school activities or student presence.",
      icon: UserCheck,
      badge: "Campus Safeguard",
      color: "text-blue-400 border-blue-500/30 bg-blue-500/10",
      legalDoc: "safeguarding" as const
    }
  ];

  return (
    <section id="trust" className="py-24 bg-[#070A11] relative border-t border-white/5">
      {/* Background glow */}
      <div className="absolute top-1/2 right-1/4 w-[600px] h-[500px] bg-cyan-500/5 rounded-full blur-[160px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto space-y-4 mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-900 border border-white/10 text-xs font-mono text-cyan-400 uppercase tracking-widest">
            <Lock className="w-3.5 h-3.5" />
            ENTERPRISE GOVERNANCE &amp; TRUST
          </div>
          <h2 className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight">
            Security, Privacy &amp; Total School Protection.
          </h2>
          <p className="text-slate-400 text-base sm:text-lg">
            Built from day one to give school boards, principals, and parent associations absolute confidence. Minor data is shielded by law; physical campuses are shielded by mathematics.
          </p>
        </div>

        {/* 6 Security Pillars Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {securityPillars.map((pillar, idx) => {
            const Icon = pillar.icon;
            return (
              <div
                key={idx}
                className="rounded-2xl p-6 bg-[#0F172A] border border-white/10 hover:border-white/20 transition-all duration-300 flex flex-col justify-between space-y-4 shadow-xl"
              >
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${pillar.color}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-slate-800 text-slate-300 border border-white/5">
                      {pillar.badge}
                    </span>
                  </div>

                  <h3 className="font-outfit text-lg font-bold text-white">
                    {pillar.title}
                  </h3>

                  <p className="text-xs text-slate-300 leading-relaxed">
                    {pillar.description}
                  </p>
                </div>

                <div className="pt-3 border-t border-white/5">
                  <button
                    onClick={() => onOpenLegal(pillar.legalDoc)}
                    className="text-xs font-mono text-cyan-400 hover:text-cyan-300 flex items-center gap-1 group"
                  >
                    <span>Read Policy Framework</span>
                    <span className="group-hover:translate-x-1 transition-transform">→</span>
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Statutory Compliance Footer Banner */}
        <div className="rounded-2xl p-6 bg-slate-900/80 border border-cyan-500/20 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs font-mono text-slate-300">
          <div className="flex items-center gap-3">
            <ShieldCheck className="w-6 h-6 text-cyan-400 shrink-0" />
            <span>
              All data hosted exclusively on Indian Sovereign Cloud data centers. Encrypted via TLS 1.3 &amp; AES-256.
            </span>
          </div>

          <div className="flex items-center gap-4 shrink-0">
            <button
              onClick={() => onOpenLegal("privacy")}
              className="text-amber-400 hover:underline"
            >
              Privacy Policy
            </button>
            <span>•</span>
            <button
              onClick={() => onOpenLegal("terms")}
              className="text-amber-400 hover:underline"
            >
              Terms of Service
            </button>
            <span>•</span>
            <button
              onClick={() => onOpenLegal("safeguarding")}
              className="text-amber-400 hover:underline"
            >
              POCSO Safeguarding
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
